import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Material UI
import { Divider, List } from '@material-ui/core';

// Components
import RecursiveListNode from './RecursiveListNode';
import { menuItemFilter, routeList, cerrarSesion } from '../../routes/routeList';

// Styles
import LogoN from '../../logos/LogoN.svg';
import LogoNovaLight from '../../logos/LogoNovaLight.svg';
import '../../styles/menu.css';
import StyledDrawer from './StyledDrawer';
import { getLogo } from '../../logos';

const THEME_COLORS = {
    tecnoTheme:   { selectedBg: '#253139', iconActive: '#EB7D1E', iconDefault: '#FFFFFF' },
    posTheme:     { selectedBg: '#00638D', iconActive: '#118DFF', iconDefault: '#FFFFFF' },
    colTheme:     { selectedBg: '#3F4A51', iconActive: '#EB7D1E', iconDefault: '#FFFFFF' },
    matteTheme:   { selectedBg: '#253139', iconActive: '#EB7D1E', iconDefault: '#FFFFFF' },
    skyTheme:     { selectedBg: '#001B69', iconActive: '#118DFF', iconDefault: '#FFFFFF' },
    vaisandTheme: { selectedBg: '#001F66', iconActive: '#FF400D', iconDefault: '#FFFFFF' },
};

const defaultColors = { selectedBg: '#472A7A', iconActive: '#FFC800', iconDefault: '#C8AFE8' };

function findInitialState(pathname) {
    const allRoutes = [...routeList, ...cerrarSesion];
    for (const item of allRoutes) {
        if (item.path === pathname) return { selectedId: item.id, openId: undefined };
        if (item.children) {
            for (const child of item.children) {
                if (child.path === pathname) return { selectedId: child.id, openId: item.id };
            }
        }
    }
    return { selectedId: undefined, openId: undefined };
}

const Menu = () => {
    const location = useLocation();
    const initial = findInitialState(location.pathname);

    const [selectedItem, setSelectedItem] = useState(initial.selectedId);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [openItem, setOpenItem] = useState(initial.openId);

    useEffect(() => {
        const colors = THEME_COLORS[process.env.REACT_APP_THEME] ?? defaultColors;
        document.documentElement.style.setProperty('--menu-selected-bg', colors.selectedBg);
        document.documentElement.style.setProperty('--menu-icon-active', colors.iconActive);
        document.documentElement.style.setProperty('--menu-icon-default', colors.iconDefault);
    }, []);

    useEffect(() => {
        const duration = 300;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            window.dispatchEvent(new Event('resize'));
            if (now - start < duration) {
                raf = requestAnimationFrame(tick);
            }
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [drawerOpen]);

    const logo = drawerOpen ? getLogo(process.env.REACT_APP_THEME) : LogoN;
    const showSecondaryLogo =
        (process.env.REACT_APP_THEME === 'tecnoTheme' ||
            process.env.REACT_APP_THEME === 'posTheme' ||
            process.env.REACT_APP_THEME === 'vaisandTheme') &&
        drawerOpen;

    const isPosTheme = process.env.REACT_APP_THEME === 'posTheme';

    return (
        <div className="menuView">
            <StyledDrawer open={drawerOpen}>
                <div
                    className={`menu-logo__wrapper${drawerOpen ? '' : ' closed'}`}
                    onClick={() => setDrawerOpen(!drawerOpen)}
                >
                    <img
                        src={logo}
                        className={`logo-pp-${process.env.REACT_APP_THEME}`}
                        alt="logo-pp"
                    />
                </div>
                <Divider />
                <List
                    component="div"
                    disablePadding
                    className={isPosTheme ? 'menu-main-list menu-main-list--hidden' : 'menu-main-list'}
                >
                    {routeList
                        .filter((item) => {
                            return (
                                (!Object.keys(menuItemFilter).includes(item.id) ||
                                    menuItemFilter[item.id]) &&
                                item.id !== 'build-info'
                            );
                        })
                        .map((item) => ({
                            ...item,
                            children: item.children?.filter(
                                (child) =>
                                    !Object.keys(menuItemFilter).includes(child.id) ||
                                    menuItemFilter[child.id]
                            ),
                        }))
                        .filter((item) => !item.children || item.children.length > 0)
                        .map((item, idx) => (
                            <RecursiveListNode
                                node={item}
                                key={idx}
                                drawerOpen={drawerOpen}
                                openItem={openItem}
                                setDrawerOpen={setDrawerOpen}
                                setOpenItem={setOpenItem}
                                selectedItem={selectedItem}
                                setSelectedItem={setSelectedItem}
                            />
                        ))}
                </List>

                <div className="menu-logo-nova__wrapper">
                    {showSecondaryLogo && (
                        <div>
                            <img src={LogoNovaLight} className="logo-nova" alt="logo-nova" />
                        </div>
                    )}
                </div>
                <Divider />
                <div className="LogOut">
                    <Divider />
                    <List component="div" disablePadding>
                        {cerrarSesion.map((item, idx) => (
                            <RecursiveListNode
                                node={item}
                                key={idx}
                                drawerOpen={drawerOpen}
                                openItem={openItem}
                                setOpenItem={setOpenItem}
                                selectedItem={selectedItem}
                                setSelectedItem={setSelectedItem}
                            />
                        ))}
                    </List>
                </div>
            </StyledDrawer>
        </div>
    );
};

export default Menu;
